import { useMemo } from 'react';

import { toast } from 'react-toastify';

import { Deal } from '../gql/dealGql';
import {
  DealMediaTypeEnum,
  DealType,
  JurisdictionQueryVariables,
  Maybe,
  StateAbbreviation,
  TitleRegistrationOption,
  TtDocumentName,
  TtProductType,
  useJurisdictionQuery,
} from '../gql/generated/graphql';
import { TTValidationName } from '../gql/taterTitleGql';
import {
  JURISDICTION_NOT_FOUND_ERROR_CODE,
  JURISDICTION_UPSTREAM_ERROR_CODE,
  hasGraphqlErrorCode,
} from '../utils/graphqlErrors';

import { isCanada } from '../constants/countries';
import { isValidStateAbbreviation } from '../libs/states';
import { DealMedia, DriverLicenseMetadata } from '../types/media';
import { findBankSlug } from '../utils/financialInfos';
import { mapTTDocumentType } from '../utils/media';

import { useBanks } from './useBanks';

const getMinDaysBeforeRegistrationExpires = (
  registrationValidations: TTValidationName[],
): number | null => {
  if (registrationValidations.includes(TTValidationName.RegistrationNotExpired90Days)) {
    return 90;
  }
  if (registrationValidations.includes(TTValidationName.RegistrationNotExpired60Days)) {
    return 60;
  }
  if (registrationValidations.includes(TTValidationName.RegistrationNotExpired)) {
    return 0;
  }
  return null;
};

type UseJurisdictionProps = {
  deal: Deal;
  media?: Maybe<Maybe<DealMedia>>[] | null;
};

export const useJurisdictionData = ({ deal, media }: UseJurisdictionProps) => {
  const { banks } = useBanks();

  // We skip if no `media` so we don't query jurisdiction twice:  first with customer state and address,
  // and second with driver license state and address.
  const skip = useMemo(
    () =>
      isCanada(deal.country) ||
      !deal.customer?.address?.state ||
      !deal.car?.year ||
      deal.type === DealType.Acquisition ||
      deal.type === DealType.Marketplace ||
      !media ||
      banks.length === 0,
    [deal.country, deal.customer?.address?.state, deal.car?.year, deal.type, media, banks],
  );

  const frontOfDriverLicenseMetadata = useMemo(
    () =>
      (media?.find((m) => m?.type === DealMediaTypeEnum.FrontOfDriversLicense)?.metadata ??
        {}) as DriverLicenseMetadata,
    [media],
  );

  const variables = useMemo(
    (): JurisdictionQueryVariables => ({
      state: deal.customer?.address?.state as StateAbbreviation,
      // Backend treats empty/whitespace county as null; pass through directly.
      county: deal.customer?.address?.county,
      conditions: {
        // Send null if empty string. Empty string causes an error.
        lienholder: deal.car?.payoff?.lienholder_slug || null,
        hasCobuyer: !!deal.cobuyer?.first_name,
        driversLicenseAddress:
          frontOfDriverLicenseMetadata.address || deal.customer?.address?.address_line,
        currentAddress: deal.customer?.address?.address_line,
        vehicleAge: new Date().getFullYear() - parseInt(deal.car?.year || '0', 10),
        driversLicenseState: isValidStateAbbreviation(frontOfDriverLicenseMetadata.state)
          ? frontOfDriverLicenseMetadata.state
          : deal.customer?.address?.state,
        movedStateDuringLease: !!deal.customer?.address?.moved_states,
        transferringPlates: !!deal.financial_info?.plate_transfer,
        transferringRegistration:
          deal.financial_info?.title_registration_option ===
          TitleRegistrationOption.TitleAndRegistrationTransfer,
        jurisdictionState: deal.customer?.address?.state,
        canVerifyDigitalSignatures: !!deal.signing_on_com,
        newBank: findBankSlug({ banks, bankName: deal.financial_info?.bank }),
        buyerIsNotLessee: !!deal.financial_info?.buyer_not_lessee,
        payoffIncludesSalesTax: !!deal.car?.payoff?.payoff_includes_sales_tax,
      },
      types: [
        TtProductType.Title,
        ...(deal.financial_info?.title_registration_option !== TitleRegistrationOption.TitleOnly
          ? [TtProductType.Registration]
          : []),
        ...(deal.financial_info?.buyer_not_lessee ? [TtProductType.Payoff] : []),
      ],
    }),
    [
      deal.customer?.address?.state,
      deal.customer?.address?.county,
      deal.car?.payoff?.lienholder_slug,
      deal.cobuyer?.first_name,
      frontOfDriverLicenseMetadata.address,
      deal.customer?.address?.address_line,
      deal.car?.year,
      frontOfDriverLicenseMetadata.state,
      deal.customer?.address?.moved_states,
      deal.financial_info?.plate_transfer,
      deal.financial_info?.title_registration_option,
      deal.signing_on_com,
      deal.financial_info?.bank,
      banks,
      deal.financial_info?.buyer_not_lessee,
      deal.car?.payoff?.payoff_includes_sales_tax,
    ],
  );

  const jurisdiction = useJurisdictionQuery({
    skip,
    variables,
    onError: (error) => {
      // The query degrades gracefully (no jurisdiction => no required documents);
      // surface a diagnostic toast so the user knows why titling data is missing.
      if (hasGraphqlErrorCode(error, JURISDICTION_UPSTREAM_ERROR_CODE)) {
        toast.error('Jurisdiction service is temporarily unavailable. Please try again shortly.', {
          toastId: 'jurisdiction-upstream-error',
        });
      } else if (hasGraphqlErrorCode(error, JURISDICTION_NOT_FOUND_ERROR_CODE)) {
        toast.warning(
          "We couldn't determine the titling jurisdiction for this address. Double-check the customer's state and county.",
          { toastId: 'jurisdiction-not-found' },
        );
      }
    },
  });

  const { requiredDocuments, validations, registrationValidations } = useMemo(
    () =>
      (jurisdiction.data?.jurisdiction?.products?.items ?? []).reduce(
        (acc, productItem) => {
          const productItemDocumentNames =
            productItem?.documents?.items
              ?.map((documentItem) =>
                mapTTDocumentType(productItem?.type, documentItem?.type?.name),
              )
              .filter(
                (item) =>
                  item &&
                  // Don't add the same document twice.
                  !acc.requiredDocuments.some((requiredDocument) => requiredDocument === item),
              ) ?? [];

          // `License` and `Insurance` validations come at Product level.
          const productItemValidationNames =
            productItem?.validations
              ?.map((validation) => validation?.name as TTValidationName)
              .filter(
                (item) =>
                  item &&
                  // Don't add the same validation twice.
                  !acc.validations.some((validation) => validation === item),
              ) ?? [];

          // `Registration` validations come at Document level.
          const registrationValidationNames =
            productItem?.documents?.items
              ?.find((documentItem) => documentItem?.type?.name === TtDocumentName.Registration)
              ?.validations?.map((validation) => validation?.name as TTValidationName)
              .filter(
                (item) =>
                  item &&
                  // Don't add the same registration validation twice.
                  !acc.registrationValidations.some(
                    (registrationValidation) => registrationValidation === item,
                  ),
              ) ?? [];

          return {
            requiredDocuments: [...acc.requiredDocuments, ...productItemDocumentNames],
            validations: [...acc.validations, ...productItemValidationNames],
            registrationValidations: [
              ...acc.registrationValidations,
              ...registrationValidationNames,
            ],
          };
        },
        {
          requiredDocuments: [] as string[],
          validations: [] as TTValidationName[],
          registrationValidations: [] as TTValidationName[],
        },
      ),
    [jurisdiction.data?.jurisdiction],
  );

  const minDaysBeforeRegistrationExpires = useMemo(
    () => getMinDaysBeforeRegistrationExpires(registrationValidations),
    [registrationValidations],
  );

  return {
    conditions: variables.conditions,
    jurisdiction: jurisdiction.data?.jurisdiction ?? jurisdiction.previousData?.jurisdiction,
    jurisdictionLoading: jurisdiction.loading,
    requiredDocuments,
    validations,
    registrationValidations,
    minDaysBeforeRegistrationExpires,
  };
};
